import React, { useState, useCallback, useEffect } from "react";

import { Plus } from "icons";

import cdesApi from "apis/cdeDefinitions";
import {
  Button,
  PageLoader,
  SubHeader,
  Typography,
} from "clearsense-ui/components";
import ContentBox from "components/Common/ContentBox";
import FilterHeader from "components/Common/FilterHeader";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import QuickFilter from "components/Common/QuickFilter";
import SummaryBarChart from "components/Common/SummaryBarChart";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import CDETable from "./CDETable";
import DeleteCDEs from "./DeleteCDEs";
import NewCDE from "./NewCDE";

import { COLUMN_DATA, QUICK_FILTER_DATA } from "../../constants/cdesTable";

const CDEDefinitionsHome = ({ userPermissions = [] }) => {
  const [isAddNewCDEOpen, setIsAddNewCDEOpen] = useState(false);
  const [CDEsData, setCDEsData] = useState([]);
  const [CDELength, setCDELength] = useState(0);
  const [checkedRows, setCheckedRows] = useState([]);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [editedRow, setEditedRow] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterConditions, setFilterConditions] = useState([]);
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});
  const [filter, setFilter] = useState({
    filterBy: "",
    filterValue: "",
  });
  const [quickFilterData, setQuickFilterData] = useState([]);
  const [sort, setSort] = useState("top");
  const [isGraphLoading, setIsGraphLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);

  const handleEdit = record => {
    setIsEdit(true);
    setEditedRow(record);
    setIsAddNewCDEOpen(true);
  };

  const handleDelete = record => {
    setRowToBeDeleted(record);
    setIsDeleteAlertOpen(true);
  };

  const fetchSummary = async () => {
    try {
      setIsGraphLoading(true);
      const { data } = await cdesApi.fetchSummary(sort);
      setSummaryData(data?.summary_data);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsGraphLoading(false);
    }
  };

  const fetchCDEs = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const { data } = await cdesApi.fetchCdes(
        search.toLowerCase(),
        currentPage,
        pageSize,
        filter.filterBy,
        filter.filterValue
      );
      setCDEsData(data?.cde_definitions?.cdes);
      setCDELength(data?.cde_definitions?.cde_count);
      setQuickFilterData(
        QUICK_FILTER_DATA(
          setFilter,
          data?.cde_definitions?.missing_description_count,
          data?.cde_definitions?.incomplete_mapping_count
        )
      );
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchSummary();
  }, [sort]);

  const filterParams = useSearchAndPaginate(fetchCDEs, {
    page: () => setCheckedRows([]),
    pageSize: () => setCheckedRows([]),
  });

  const columnHeaderSelector = useColumnHeaderSelector(
    COLUMN_DATA(handleEdit, handleDelete, userPermissions)
  );

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <SubHeader
        className="mt-8"
        breadcrumbs={[
          { label: "CDE Definitions", link: "#" },
          { label: "Overview", link: "#" },
        ]}
        actionBlock={
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="read"
          >
            <Button
              label="Create Critical Data Element"
              icon={Plus}
              data-cy="cde-create-button"
              onClick={() => {
                setIsAddNewCDEOpen(true);
                setIsEdit(false);
              }}
            />
          </PermissibleWrapper>
        }
      />
      <ContentBox className="flex flex-col justify-center space-y-4 mt-4 items-center bg-white dark:bg-[#2f3941] w-full dark:text-white">
        <Typography type="semibold" style="h3" className="justify-center flex">
          Summary
        </Typography>
        <div className="flex space-x-4 w-full">
          <QuickFilter className="w-1/2" quickFilterData={quickFilterData} />
          <SummaryBarChart
            className="w-1/2"
            title="Critical Data Element Mapping Progress"
            isLoading={isGraphLoading}
            data={summaryData}
            topFiveAction={() => setSort("top")}
            bottomFiveAction={() => setSort("bottom")}
          />
        </div>
      </ContentBox>
      <FilterHeader
        entity="CDE"
        filterParams={filterParams}
        columnHeaderSelector={columnHeaderSelector}
        count={CDELength}
        filterConditions={filterConditions}
        setFilterConditions={setFilterConditions}
        checkedRows={checkedRows}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        userPermissions={userPermissions}
        columnOptions={COLUMN_DATA().map(column => column.title)}
      />
      {isLoading ? (
        <PageLoader />
      ) : (
        <CDETable
          CDEsData={CDEsData}
          selectedColumns={columnHeaderSelector.columns.selected}
          filterParams={filterParams}
          CDELength={CDELength}
          checkedRows={checkedRows}
          setCheckedRows={setCheckedRows}
          userPermissions={userPermissions}
          setIsAddNewCDEOpen={setIsAddNewCDEOpen}
          setIsEdit={setIsEdit}
        />
      )}
      <NewCDE
        isPaneOpen={isAddNewCDEOpen}
        setIsPaneOpen={setIsAddNewCDEOpen}
        fetchCdesData={fetchCDEs}
        filterParams={filterParams}
        setCheckedRows={setCheckedRows}
        setIsTableLoading={setIsLoading}
        isEdit={isEdit}
        editedRow={editedRow}
      />
      <DeleteCDEs
        checkedRows={checkedRows}
        isDeleteAlertOpen={isDeleteAlertOpen}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        fetchCDEs={fetchCDEs}
        filterParams={filterParams}
        rowToBeDeleted={rowToBeDeleted}
        setRowToBeDeleted={setRowToBeDeleted}
        setCheckedRows={setCheckedRows}
        setIsLoading={setIsLoading}
      />
    </div>
  );
};

export default CDEDefinitionsHome;
